import { Controller, Get, Query, UseGuards } from '@nestjs/common';
import { ApiTags, ApiOperation, ApiResponse, ApiBearerAuth, ApiQuery } from '@nestjs/swagger';
import { StatisticsService } from '../services/statistics.service';
import { CustomerStatisticsService } from '../services/customer-statistics.service';
import { JwtAuthGuard } from '@/common/guards/jwt-auth.guard';

@ApiTags('Statistics')
@Controller('statistics')
@UseGuards(JwtAuthGuard)
@ApiBearerAuth()
export class StatisticsController {
  constructor(
    private readonly statisticsService: StatisticsService,
    private readonly customerStatisticsService: CustomerStatisticsService,
  ) {}

  @UseGuards(JwtAuthGuard)
  @Get('dashboard')
  @ApiOperation({ summary: 'Get comprehensive dashboard statistics' })
  @ApiResponse({ status: 200, description: 'Dashboard statistics retrieved successfully' })
  async getDashboardStatistics() {
    return this.statisticsService.getDashboardStatistics();
  }

  @UseGuards(JwtAuthGuard)
  @Get('revenue')
  @ApiOperation({ summary: 'Get revenue statistics (today, month, year)' })
  @ApiResponse({ status: 200, description: 'Revenue statistics retrieved successfully' })
  async getRevenueStatistics() {
    return this.statisticsService.getRevenueStatistics();
  }

  @Get('top-products')
  @ApiOperation({ summary: 'Get top selling products' })
  @ApiQuery({ name: 'limit', required: false, type: Number, description: 'Number of products to return' })
  @ApiResponse({ status: 200, description: 'Top products retrieved successfully' })
  async getTopProducts(@Query('limit') limit?: number) {
    return this.statisticsService.getTopSellingProducts(limit ? Number(limit) : 10);
  }

  @Get('top-packages')
  @ApiOperation({ summary: 'Get top selling packages' })
  @ApiQuery({ name: 'limit', required: false, type: Number, description: 'Number of packages to return' })
  @ApiResponse({ status: 200, description: 'Top packages retrieved successfully' })
  async getTopPackages(@Query('limit') limit?: number) {
    return this.statisticsService.getTopSellingPackages(limit ? Number(limit) : 10);
  }

  @Get('top-customers-orders')
  @ApiOperation({ summary: 'Get top customers by order count' })
  @ApiQuery({ name: 'limit', required: false, type: Number, description: 'Number of customers to return' })
  @ApiResponse({ status: 200, description: 'Top customers by orders retrieved successfully' })
  async getTopCustomersByOrders(@Query('limit') limit?: number) {
    return this.statisticsService.getTopCustomersByOrders(limit ? Number(limit) : 10);
  }

  @Get('top-customers-amount')
  @ApiOperation({ summary: 'Get top customers by total amount spent' })
  @ApiQuery({ name: 'limit', required: false, type: Number, description: 'Number of customers to return' })
  @ApiResponse({ status: 200, description: 'Top customers by amount retrieved successfully' })
  async getTopCustomersByAmount(@Query('limit') limit?: number) {
    return this.statisticsService.getTopCustomersByAmount(limit ? Number(limit) : 10);
  }

  @Get('top-states')
  @ApiOperation({ summary: 'Get top states/provinces by sales' })
  @ApiQuery({ name: 'limit', required: false, type: Number, description: 'Number of states to return' })
  @ApiResponse({ status: 200, description: 'Top states retrieved successfully' })
  async getTopStates(@Query('limit') limit?: number) {
    return this.statisticsService.getTopStatesBySales(limit ? Number(limit) : 10);
  }

  @Get('total-customers')
  @ApiOperation({ summary: 'Get total number of customers' })
  @ApiResponse({ status: 200, description: 'Total customers count retrieved successfully' })
  async getTotalCustomers() {
    return {
      total: await this.statisticsService.getTotalCustomers(),
    };
  }

  @Get('customer/:phone')
  @ApiOperation({ summary: 'Get customer statistics by phone number' })
  @ApiResponse({ status: 200, description: 'Customer statistics retrieved successfully' })
  async getCustomerByPhone(@Query('phone') phone: string) {
    return this.customerStatisticsService.getCustomerByPhone(phone);
  }

  @Get('orders-by-date-range')
  @ApiOperation({ summary: 'Get order statistics by date range' })
  @ApiQuery({ name: 'startDate', required: true, type: String, description: 'Start date (ISO string)' })
  @ApiQuery({ name: 'endDate', required: true, type: String, description: 'End date (ISO string)' })
  @ApiResponse({ status: 200, description: 'Order statistics retrieved successfully' })
  async getOrderStatisticsByDateRange(
    @Query('startDate') startDate: string,
    @Query('endDate') endDate: string,
  ) {
    return this.statisticsService.getOrderStatisticsByDateRange(
      new Date(startDate),
      new Date(endDate),
    );
  }

  @Get('order-count')
  @ApiOperation({ summary: 'Get order count statistics' })
  @ApiResponse({ status: 200, description: 'Order count statistics retrieved successfully' })
  async getOrderCountStatistics() {
    return this.statisticsService.getOrderCountStatistics();
  }

  @Get('average-order-value')
  @ApiOperation({ summary: 'Get average order value statistics' })
  @ApiResponse({ status: 200, description: 'Average order value retrieved successfully' })
  async getAverageOrderValue() {
    return this.statisticsService.getAverageOrderValue();
  }

  @Get('sales-trend')
  @ApiOperation({ summary: 'Get sales trend for last N days' })
  @ApiQuery({ name: 'days', required: false, type: Number, description: 'Number of days (default: 7)' })
  @ApiResponse({ status: 200, description: 'Sales trend retrieved successfully' })
  async getSalesTrend(@Query('days') days?: number) {
    return this.statisticsService.getSalesTrend(days ? Number(days) : 7);
  }

  @Get('payment-methods')
  @ApiOperation({ summary: 'Get payment method statistics' })
  @ApiResponse({ status: 200, description: 'Payment method statistics retrieved successfully' })
  async getPaymentMethodStatistics() {
    return this.statisticsService.getPaymentMethodStatistics();
  }

  @Get('profit')
  @ApiOperation({ summary: 'Get profit statistics with date filtering (day, month, year, or custom range)' })
  @ApiQuery({ name: 'period', required: false, enum: ['day', 'month', 'year'], description: 'Period type: day, month, or year' })
  @ApiQuery({ name: 'startDate', required: false, type: String, description: 'Start date (ISO string) for custom range' })
  @ApiQuery({ name: 'endDate', required: false, type: String, description: 'End date (ISO string) for custom range' })
  @ApiResponse({ status: 200, description: 'Profit statistics retrieved successfully' })
  async getProfitStatistics(
    @Query('period') period?: 'day' | 'month' | 'year',
    @Query('startDate') startDate?: string,
    @Query('endDate') endDate?: string,
  ) {
    const start = startDate ? new Date(startDate) : undefined;
    const end = endDate ? new Date(endDate) : undefined;
    return this.statisticsService.getProfitStatistics(period, start, end);
  }

  @Get('ecommerce')
  @ApiOperation({ summary: 'Get comprehensive ecommerce statistics with date filtering' })
  @ApiQuery({ name: 'period', required: false, enum: ['day', 'month', 'year'], description: 'Period type: day, month, or year' })
  @ApiQuery({ name: 'startDate', required: false, type: String, description: 'Start date (ISO string) for custom range' })
  @ApiQuery({ name: 'endDate', required: false, type: String, description: 'End date (ISO string) for custom range' })
  @ApiResponse({ status: 200, description: 'Ecommerce statistics retrieved successfully' })
  async getEcommerceStatistics(
    @Query('period') period?: 'day' | 'month' | 'year',
    @Query('startDate') startDate?: string,
    @Query('endDate') endDate?: string,
  ) {
    const start = startDate ? new Date(startDate) : undefined;
    const end = endDate ? new Date(endDate) : undefined;
    return this.statisticsService.getEcommerceStatistics(period, start, end);
  }

  @Get('recalculate')
  @ApiOperation({ summary: 'Recalculate all statistics from scratch (products soldCount, packages soldCount, customer statistics)' })
  @ApiResponse({ status: 200, description: 'Statistics recalculated successfully' })
  async recalculateAllStatistics() {
    return this.statisticsService.recalculateAllStatistics();
  }
}

